import { lazy, Suspense } from "react";
import { Outlet, RouterProvider, createBrowserRouter, redirect, type RouteObject } from "react-router";
import GeneralError from "./components/Layout/Error/GeneralError";
import ConnectEmailCallback from "./components/Auth/ConnectEmailCallback";
import PublicViewComparisonPage from "./components/Compare/View/PublicViewComparisonPage";
import z from "zod";
import { queryClient, QueryOptions } from "./clients/ReactQueryClient.ts";
import PurchaserClient from "./clients/PurchaserClient.ts";
import { parseRFQFilterParams } from "./components/RFQ/List/useRFQListFilters";
import type { RFQListResponse } from "./clients/Purchaser/RFQ.ts";
import { parseCatalogFilterParams } from "./components/ProductCatalog/List/useCatalogListFilters";
import { parseSourcingRequestFilterParams } from "./components/SourcingRequests/List/useSourcingRequestsFilters";
import IndividualEntityPage from "./components/Layout/Page/IndividualEntityPage";
import MagicToast from "./components/DesignSystem/MagicToast";
import Hubspot from "./util/Hubspot";
import FeatureGate from "./components/Layout/FeatureGate";
import { FEATURE_KEYS } from "./util/useFeatureAccess";

// Layout pages
const SidebarMenuPage = lazy(() => import("./components/Layout/Page/SidebarMenuPage"));
const ProtectedRoute = lazy(() => import("./components/Layout/ProtectedRoute"));

// Error pages
const NotFound = lazy(() => import("./components/Layout/Error/404"));
const Maintenance = lazy(() => import("./components/Layout/Error/Maintenance"));

// Auth pages
const Login = lazy(() => import("./components/Auth/Login"));
const ForgotPassword = lazy(() => import("./components/Auth/Password/ForgotPassword"));
const Register = lazy(() => import("./components/Auth/Register"));
const ResetPassword = lazy(() => import("./components/Auth/Password/ResetPassword"));
const UserSettingsPage = lazy(() => import("./components/Auth/UserSettingsPage"));

// Team pages
const TeamMembersPage = lazy(() => import("./components/Settings/Team/TeamMembers"));
const CompanyDetailsPage = lazy(() => import("./components/Settings/Team/CompanyDetails"));
const UsageAndBilling = lazy(() => import("./components/UsageAndBilling/Page"));

// RFQ pages
const RFQ = lazy(() => import("./components/RFQ/RFQ"));
const RFQLineItems = lazy(() => import("./components/RFQ/RFQLineItems"));
const RFQCompose = lazy(() => import("./components/RFQ/RFQCompose"));
const RFQConfigure = lazy(() => import("./components/RFQ/RFQConfigure"));
const RFQSummary = lazy(() => import("./components/RFQ/RFQSummary"));
const RFQAwards = lazy(() => import("./components/RFQ/RFQAwards.tsx"));
const RFQInbox = lazy(() => import("./components/RFQ/PostSend/InboxV2/Inbox.tsx"));
const RFQQuotes = lazy(() => import("./components/RFQ/PostSend/Quotes/RFQQuotes.tsx"));
const RFQComparison = lazy(() => import("./components/RFQ/PostSend/Quotes/Comparison"));
const RFQComparisonLineItems = lazy(() => import("./components/RFQ/PostSend/Quotes/QuotesItems"));
const RFQList = lazy(() => import("./components/RFQ/List/RFQList"));
const CreateRFQRedirect = lazy(() => import("./components/RFQ/RFQNew"));
const MailboxSources = lazy(() => import("./components/RFQ/PostSend/Tabular/MailboxSources"));
const MailboxItems = lazy(() => import("./components/RFQ/PostSend/Tabular/MailboxItems"));

// RFQ Awards
const AwardsAwarded = lazy(() => import("./components/RFQ/PostSend/Awards/AwardsAwarded.tsx"));
const AwardsPending = lazy(() => import("./components/RFQ/PostSend/Awards/AwardsPending.tsx"));
const AwardsRejected = lazy(() => import("./components/RFQ/PostSend/Awards/AwardsRejected.tsx"));

// RFQ Templates pages
const TemplateView = lazy(() => import("./components/RFQ/Templates/TemplateView"));

// Contacts and Vendors
const Contacts = lazy(() => import("./components/ContactsAndVendors/Contacts"));
const Vendors = lazy(() => import("./components/ContactsAndVendors/Vendors/Vendors"));
const VendorEdit = lazy(() => import("./components/ContactsAndVendors/Vendors/VendorEdit"));
const ContactLists = lazy(() => import("./components/ContactsAndVendors/ContactLists"));
const ViewContactList = lazy(() => import("./components/ContactsAndVendors/ViewContactList"));
const ImportStart = lazy(() => import("./components/ContactsAndVendors/Imports/ImportStart"));
const ManageContactVendorAttributes = lazy(() => import("./components/ContactsAndVendors/Manage/AttributesPage"));
const AllVendorPerformanceDashboard = lazy(() => import("./components/ContactsAndVendors/Vendors/AllVendorPerformanceDashboard"));

// Compare
const ViewComparison = lazy(() => import("./components/Compare/View/ViewComparisonPage"));
const Comparisons = lazy(() => import("./components/Compare/Comparisons"));
const CreateComparison = lazy(() => import("./components/Compare/CreateComparison"));

// Product Catalog
const CatalogList = lazy(() => import("./components/ProductCatalog/List/CatalogList"));
const CatalogItemView = lazy(() => import("./components/ProductCatalog/View/CatalogItemView"));

// Sourcing Requests
const SourcingRequestsList = lazy(() => import("./components/SourcingRequests/List/SourcingRequestsList"));
const SourcingRequestView = lazy(() => import("./components/SourcingRequests/View/SourcingRequestView"));

const VendorDiscovery = lazy(() => import("./components/VendorDiscovery/VendorDiscoveryPage"));
const VendorDiscoveryFullPage = lazy(() => import("./components/VendorDiscovery/VendorDiscoveryFullPage"));
const Home = lazy(() => import("./components/Home/index"));
const CustomizableHome = lazy(() => import("./components/Home/CustomizableHome"));
const WidgetShowcase = lazy(() => import("./components/Dashboard/WidgetShowcase"));
const CustomDashboard = lazy(() => import("./components/Dashboard/CustomDashboard"));
const DashboardIndex = lazy(() => import("./components/Dashboard/DashboardIndex"));

// Checkout
const Checkout = lazy(() => import("./components/PricingAndCheckout/Checkout"));
const Pricing = lazy(() => import("./components/PricingAndCheckout/Pricing"));

// Documents Hub (DH)
const DHDocuments = lazy(() => import("./components/DocumentsHub/Hub/Documents"));
const DHDocument = lazy(() => import("./components/DocumentsHub/Document/Document"));

// Document Viewer (Single Document)
const Document = lazy(() => import("./components/Documents/Document"));

// Settings
const Settings = lazy(() => import("./components/Settings/Settings"));

// Unsubscribe
const Unsubscribe = lazy(() => import("./components/Unsubscribe"));

// Outreach
const Outreach = lazy(() => import("./components/Outreach"));

declare global {
    // Used in Vendor Discovery Map component
    namespace google {
        const maps: any;
    }
}

export default function App() {
    // Unprotected routes that do not require authentication
    const unprotectedRoutes: RouteObject[] = [
        {
            path: "/login",
            element: <Login />,
        },
        {
            path: "/registration-internal-link/*",
            element: <Register />,
        },
        {
            path: "/forgot-password",
            element: <ForgotPassword />,
        },
        {
            path: "/reset-password",
            element: <ResetPassword />,
        },
        {
            path: "pricing",
            element: <Pricing />,
        },
        {
            path: "/checkout",
            element: <Checkout />,
        },
        {
            path: "/unsubscribe",
            element: <Unsubscribe />,
        },
        {
            path: "/compare/share/:shareKey",
            element: <PublicViewComparisonPage />,
        },
        {
            path: "/outreach/:uniqueUrlSlug",
            element: <Outreach />,
        },
    ];

    // Protected routes
    // These routes require authentication and will redirect to /login if not authenticated
    // They also show the sidebar menu
    const protectedSidebarRoutes: RouteObject = {
        element: (
            <ProtectedRoute>
                <SidebarMenuPage />
            </ProtectedRoute>
        ),
        children: [
            {
                path: "/",
                index: true,
                element: <Home />,
            },
            {
                path: "/home-custom",
                element: <CustomizableHome />,
            },
            {
                path: "/widget-library",
                element: <WidgetShowcase />,
            },
            {
                path: "/dashboards",
                element: <DashboardIndex />,
            },
            {
                path: "/dashboards/:dashboardId",
                element: <CustomDashboard />,
            },
            {
                path: "/compare",
                index: true,
                element: (
                    <FeatureGate feature={FEATURE_KEYS.QUOTE_COMPARE}>
                        <Comparisons />
                    </FeatureGate>
                ),
            },
            {
                path: "rfqs",
                index: true,
                element: (
                    <FeatureGate feature={FEATURE_KEYS.RFQS}>
                        <RFQList />
                    </FeatureGate>
                ),
                loader: ({ request }) => {
                    const url = new URL(request.url);
                    const filters = parseRFQFilterParams(url.searchParams);
                    queryClient.prefetchInfiniteQuery({
                        queryKey: ["rfqList", "infinite", filters],
                        queryFn: async ({ pageParam }): Promise<RFQListResponse> => {
                            return await PurchaserClient.getRFQList({ ...filters, page: pageParam, limit: 50 });
                        },
                        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
                            if (!lastPage?.pagination) return undefined;
                            const { totalPages } = lastPage.pagination;
                            return lastPageParam < totalPages ? lastPageParam + 1 : undefined;
                        },
                        initialPageParam: 1,
                        pages: 1,
                    });
                },
            },
            {
                path: "/contacts",
                children: [
                    {
                        path: "",
                        index: true,
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.RFQS}>
                                <Contacts />
                            </FeatureGate>
                        ),
                    },
                    {
                        path: "import",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.RFQS}>
                                <ImportStart />
                            </FeatureGate>
                        ),
                    },
                ],
            },
            {
                path: "/contacts/lists",
                index: true,
                element: (
                    <FeatureGate feature={FEATURE_KEYS.RFQS}>
                        <ContactLists />
                    </FeatureGate>
                ),
            },
            {
                path: "/vendors",
                element: (
                    <FeatureGate feature={FEATURE_KEYS.RFQS}>
                        <Vendors />
                    </FeatureGate>
                ),
            },
            {
                path: "/vendors/performance",
                element: <AllVendorPerformanceDashboard />,
            },
            {
                path: "/vendor-discovery",
                element: (
                    <FeatureGate feature={FEATURE_KEYS.VENDOR_DISCOVERY}>
                        <VendorDiscovery />
                    </FeatureGate>
                ),
            },
            {
                path: "/documents-hub",
                index: true,
                element: <DHDocuments />,
            },
            {
                path: "/catalog",
                index: true,
                element: <CatalogList />,
                loader: ({ request }) => {
                    const url = new URL(request.url);
                    const filters = parseCatalogFilterParams(url.searchParams);
                    queryClient.prefetchQuery(QueryOptions.CatalogList(filters));
                },
            },
            {
                path: "/sourcing-requests",
                index: true,
                element: <SourcingRequestsList />,
                loader: ({ request }) => {
                    const url = new URL(request.url);
                    const filters = parseSourcingRequestFilterParams(url.searchParams);
                    queryClient.prefetchQuery(QueryOptions.SourcingRequestList(filters));
                },
            },
        ],
    };

    // Protected routes for individual entities (no sidebar)
    const protectedIndividualEntityRoutes: RouteObject = {
        element: (
            <ProtectedRoute>
                <IndividualEntityPage />
            </ProtectedRoute>
        ),
        children: [
            {
                path: "/user-settings",
                element: <UserSettingsPage />,
            },
            {
                path: "/vendor-discovery/:uniquePublicId",
                element: (
                    <FeatureGate feature={FEATURE_KEYS.VENDOR_DISCOVERY}>
                        <VendorDiscoveryFullPage />
                    </FeatureGate>
                ),
            },
            {
                path: "/team/members",
                element: <TeamMembersPage />,
            },
            {
                path: "/team/company-details",
                element: <CompanyDetailsPage />,
            },
            {
                path: "/team/current-plan",
                element: <UsageAndBilling />,
            },
            {
                path: "/team/usage",
                element: <UsageAndBilling />,
            },
            {
                path: "/team/billing",
                element: <UsageAndBilling />,
            },

            // Quote Compare
            {
                path: "/compare",
                children: [
                    {
                        path: "create",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.QUOTE_COMPARE}>
                                <CreateComparison />
                            </FeatureGate>
                        ),
                    },
                    {
                        path: ":publicId",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.QUOTE_COMPARE}>
                                <ViewComparison />
                            </FeatureGate>
                        ),
                    },
                    {
                        path: ":publicId/line-items",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.QUOTE_COMPARE}>
                                <ViewComparison />
                            </FeatureGate>
                        ),
                    },
                ],
            },

            // RFQs
            {
                path: "rfqs",
                element: (
                    <FeatureGate feature={FEATURE_KEYS.RFQS}>
                        <RFQ />
                    </FeatureGate>
                ),
                children: [
                    {
                        // Laying out the path this way gives us the URL structure like this:
                        // /rfqs/:rfqPublicId/compose
                        // /rfqs/:rfqPublicId/configure
                        // /rfqs/:rfqPublicId
                        path: ":rfqPublicId",
                        loader: async ({ params }) => {
                            try {
                                const { rfqPublicId } = params;
                                const validId = `${z.coerce.number().parse(rfqPublicId)}`;
                                queryClient.prefetchQuery(QueryOptions.RFQ(validId));
                                queryClient.prefetchQuery(QueryOptions.RFQMailboxes(validId));
                                return { rfqPublicId: validId };
                            } catch {
                                // Redirect to RFQ list if invalid ID
                                return redirect("/rfqs");
                            }
                        },
                        children: [
                            {
                                path: "line-items",
                                element: <RFQLineItems />,
                            },
                            {
                                path: "compose",
                                element: <RFQCompose />,
                            },
                            {
                                path: "configure",
                                element: <RFQConfigure />,
                            },
                            {
                                path: "summary",
                                element: <RFQSummary />,
                            },
                            {
                                path: "inbox",
                                element: <RFQInbox />,
                            },
                            {
                                path: "inbox/:mailboxId",
                                element: <RFQInbox />,
                            },
                            {
                                path: "quotes",
                                element: <RFQQuotes />,
                            },
                            {
                                path: "awards",
                                element: <RFQAwards />,
                                children: [
                                    {
                                        index: true,
                                        loader: () => redirect("awarded"),
                                    },
                                    {
                                        path: "awarded",
                                        element: <AwardsAwarded />,
                                    },
                                    {
                                        path: "pending",
                                        element: <AwardsPending />,
                                    },
                                    {
                                        path: "rejected",
                                        element: <AwardsRejected />,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },

            {
                path: "rfqs/:rfqPublicId",
                children: [
                    {
                        path: "quotes/:vendorId",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.RFQS}>
                                <RFQQuotes />
                            </FeatureGate>
                        ),
                    },
                    {
                        path: "inbox/:mailboxId/tabular",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.RFQS}>
                                <MailboxItems />
                            </FeatureGate>
                        ),
                    },
                    {
                        path: "inbox/:mailboxId/tabular/source",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.RFQS}>
                                <MailboxSources />
                            </FeatureGate>
                        ),
                    },
                    {
                        path: "comparison",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.RFQS}>
                                <RFQComparison />
                            </FeatureGate>
                        ),
                    },
                    {
                        path: "comparison/line-items",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.RFQS}>
                                <RFQComparisonLineItems />
                            </FeatureGate>
                        ),
                    },
                ],
            },


            // Vendor View / Edit
            {
                path: "/vendors/:vendorRelationshipId",
                children: [
                    {
                        index: true,
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.RFQS}>
                                <VendorEdit />
                            </FeatureGate>
                        ),
                    },
                    {
                        path: "details",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.RFQS}>
                                <VendorEdit />
                            </FeatureGate>
                        ),
                    },
                    {
                        path: "contacts",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.RFQS}>
                                <VendorEdit />
                            </FeatureGate>
                        ),
                    },
                    {
                        path: "indexed-profile",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.RFQS}>
                                <VendorEdit />
                            </FeatureGate>
                        ),
                    },
                    {
                        path: "documents",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.RFQS}>
                                <VendorEdit />
                            </FeatureGate>
                        ),
                    },
                    {
                        path: "rfqs",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.RFQS}>
                                <VendorEdit />
                            </FeatureGate>
                        ),
                    },
                    {
                        path: "performance-dashboard",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.RFQS}>
                                <VendorEdit />
                            </FeatureGate>
                        ),
                    },
                ],
            },

            // Product Catalog Item View
            {
                path: "/catalog/:catalogItemId",
                children: [
                    {
                        index: true,
                        element: <CatalogItemView />,
                    },
                    {
                        path: "details",
                        element: <CatalogItemView />,
                    },
                    {
                        path: "rfq-history",
                        element: <CatalogItemView />,
                    },
                    {
                        path: "sourcing-requests",
                        element: <CatalogItemView />,
                    },
                ],
            },

            // Sourcing Request View
            {
                path: "/sourcing-requests/:sourcingRequestId",
                element: <SourcingRequestView />,
            },

            // Contact Lists
            {
                path: "/contacts",
                children: [
                    {
                        path: "lists/:contactListId",
                        element: (
                            <FeatureGate feature={FEATURE_KEYS.RFQS}>
                                <ViewContactList />
                            </FeatureGate>
                        ),
                    },
                ],
            },

            // Contacts and Vendors - Attributes Management
            {
                path: "/manage/attributes/contacts-vendors",
                element: (
                    <FeatureGate feature={FEATURE_KEYS.RFQS}>
                        <ManageContactVendorAttributes />
                    </FeatureGate>
                ),
            },

            // View Vendor Document
            {
                path: "/documents/:id/:base64InternalFilename",
                element: <Document />,
            },

            // Unified Settings
            {
                path: "/settings",
                children: [
                    {
                        index: true,
                        element: <Settings />,
                    },
                    {
                        path: "user/profile",
                        element: <Settings />,
                    },
                    {
                        path: "user/notifications",
                        element: <Settings />,
                    },
                    {
                        path: "user/notifications/rfq-subscriptions",
                        element: <Settings />,
                    },
                    {
                        path: "user/notifications/rfq-assignment",
                        element: <Settings />,
                    },
                    {
                        path: "rfq/labels",
                        element: <Settings />,
                    },
                    {
                        path: "rfq/attributes",
                        element: <Settings />,
                    },
                    {
                        path: "rfq/templates",
                        element: <Settings />,
                    },
                    {
                        path: "rfq/templates/:templateId",
                        element: <TemplateView />,
                    },
                    {
                        path: "rfq/webhooks",
                        element: <Settings />,
                    },
                    {
                        path: "vendors/attributes",
                        element: <Settings />,
                    },
                    {
                        path: "vendors/performance-scoring",
                        element: <Settings />,
                    },
                    {
                        path: "team/members",
                        element: <Settings />,
                    },
                    {
                        path: "team/billing",
                        element: <Settings />,
                    },
                    {
                        path: "team/usage",
                        element: <Settings />,
                    },
                    {
                        path: "team/company-details",
                        element: <Settings />,
                    },
                    {
                        path: "security/email-activity",
                        element: <Settings />,
                    },
                    {
                        path: "security/trust-center",
                        element: <Settings />,
                    },
                ],
            },

            // Document Hub Document View
            {
                path: "/documents-hub/:id/:base64InternalFilename/:originalFilename",
                element: <DHDocument />,
            },
        ],
    };

    // Additional Programmatic Routes
    // These routes are for things like redirects or routes that don't fit into the above categories
    const additionalProgrammaticRoutes: RouteObject = {
        element: (
            <ProtectedRoute>
                <Outlet />
            </ProtectedRoute>
        ),
        children: [
            // Connect your email callback
            {
                path: "/connect-email/oauth",
                element: <ConnectEmailCallback />,
            },

            // RFQ v2 to v3 Redirects
            {
                path: "/sourcing/rfqs",
                errorElement: <GeneralError backTo="/rfqs" backToLabel="RFQs" />,
                children: [
                    {
                        index: true,
                        loader: () => redirect("/rfqs"),
                    },
                    {
                        path: ":rfqPublicId",
                        loader: () => redirect("/rfqs"),
                    },
                    {
                        path: ":rfqPublicId/quotes",
                        loader: () => redirect("/rfqs"),
                    },
                    {
                        path: ":rfqPublicId/threads",
                        loader: () => redirect("/rfqs"),
                    },
                ],
            },

            // Create a new RFQ redirect
            {
                path: "/rfqs/new",
                element: <CreateRFQRedirect />,
            },
        ],
    };

    const routes: RouteObject[] = [
        {
            path: "/",
            element: (
                <Suspense>
                    <Hubspot.Loader />
                    <MagicToast.Container />
                    <Outlet />
                </Suspense>
            ),
            errorElement: <GeneralError backTo="/" backToLabel="purchaser" />,
            children: [...unprotectedRoutes, protectedSidebarRoutes, protectedIndividualEntityRoutes, additionalProgrammaticRoutes],
        },

        // Default catch all - 404
        {
            path: "/*",
            element: (
                <Suspense>
                    <NotFound />
                </Suspense>
            ),
        },
    ];

    // Maintenance Mode Routes
    // If the app is in maintenance mode these routes will be used instead
    // to show a maintenance page for all routes
    const isMaintenanceMode = false;
    const maintenanceRoutes: RouteObject[] = [
        {
            path: "/*",
            element: (
                <Suspense>
                    <Maintenance />
                </Suspense>
            ),
        },
    ];

    const router = createBrowserRouter(isMaintenanceMode ? maintenanceRoutes : routes);

    return <RouterProvider router={router} />;
}
